import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronDown,
  FileText,
  GitMerge,
  Link2,
  Loader2,
  MessageSquareQuote,
  PenLine,
  Scale,
  ScrollText,
  Sparkles,
  Users,
  Volume2,
  X,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { ApiError } from '../../lib/api';
import { Button } from '../ui/Button';
import { EsqueletoLineas } from '../ui/Esqueleto';
import { useMediaUrl } from '../../lib/media';
import { documentosApi, documentosKeys, type Bloque } from '../../lib/documentos';
import {
  AYUDA_CLASE,
  cambiosApi,
  cambiosKeys,
  ETIQUETA_CLASE,
  ETIQUETA_ESTADO,
  ETIQUETA_ESTADO_IMPACTO,
  ETIQUETA_QUE_APORTA,
  bloqueoParaAprobar,
  borradorInicial,
  diffPalabras,
  esContradiccion,
  esTerminal,
  etiquetaBloque,
  impactosDe,
  indiceCambiosPorBloque,
  resumenDiff,
  textoSegunVista,
  yaDecidido,
  type CambioConProcedencia,
  type ClaseCambio,
  type DecisionCambio,
  type GrupoDeImpactos,
  type ImpactoAbierto,
  type PosicionEnfrentada,
  type VozDelCambio,
} from '../../lib/cambios';

/**
 * DIFF CON PROCEDENCIA — las cuatro capas del SSOT §5.8.
 *
 * Por cada cambio propuesto sobre el documento:
 *
 *   1. ORIGINAL — el texto como está hoy.
 *   2. VOCES    — quién lo pidió, qué dijo y, si habló, su audio para escucharlo.
 *   3. ACUERDO  — lo que se propone y por qué. **Si la clase es contradictoria no
 *                 hay propuesta**: hay dos posiciones enfrentadas y quién sostiene
 *                 cada una. Eso NO es un hueco del sistema, es su trabajo bien
 *                 hecho — la decisión es de una persona (D05/D06).
 *   4. FINAL    — la redacción aprobada, editable antes de aprobar.
 *
 * Dos formas, una sola razón. **En escritorio**: documento a la izquierda, panel
 * del cambio a la derecha. **En móvil**: cada marca abre una tarjeta apilada con
 * las cuatro capas en acordeón. El documento entero en cuatro columnas es
 * ilegible y no se hace.
 *
 * La elección entre una y otra es por JavaScript y no por CSS a propósito: si se
 * pintaran las dos y se escondiera una, el audio de la tarjeta oculta seguiría
 * sonando —`display:none` no pausa nada— y quien está en un teléfono escucharía
 * dos veces la misma voz.
 *
 * El componente NO llama a la API: recibe los cambios y avisa la decisión hacia
 * arriba. Quién puede aprobar, cuándo se refresca y qué se hace con la
 * propagación lo decide la página. Acá vive una sola regla de negocio, y es la
 * del servidor repetida sin adornos: `puede_aprobarse` manda sobre el botón, y
 * cuando dice que no, el motivo que se muestra es el que el servidor escribió.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Props
// ═══════════════════════════════════════════════════════════════════════════

/** Lo común a las dos formas de usarlo. */
interface DiffProcedenciaBase {
  /** Cambio con el que abre. Después la selección la maneja el diff. */
  cambioId?: string | null;
  /** Volver a donde estaba quien abrió esto. Si no va, la X solo suelta la marca. */
  onCerrar?: () => void;
  /**
   * Quien facilita decide. Sin esto la vista es de consulta, con todo visible:
   * las voces y las posiciones se leen igual, que es el punto del expediente.
   * El servidor exige facilitador de todas formas; esto solo evita ofrecer un
   * botón que iba a terminar en un 403.
   */
  puedeDecidir?: boolean;
  /** Selección controlada desde afuera. Si no va, la maneja el componente. */
  seleccionado?: string | null;
  onSeleccionar?: (cambioId: string | null) => void;
}

/**
 * Forma A — **autónoma, solo primitivas**. Se le pasa la validación y el diff
 * pide su propio detalle y resuelve su propio PATCH. Es la que usa el panel de
 * la Cabina, y existe para no atar dos archivos de dueños distintos a una misma
 * forma de datos.
 */
export interface DiffProcedenciaAutonomo extends DiffProcedenciaBase {
  /** La validación (actividad). De acá salen los cambios, el documento y los impactos. */
  actividadId: string;
  cambios?: undefined;
}

/**
 * Forma B — **controlada**. Quien la monta ya tiene los datos (porque los usa
 * para otra cosa en la misma pantalla) y prefiere resolver el PATCH afuera. No
 * se hace ninguna petición: lo que llega es lo que se pinta.
 */
export interface DiffProcedenciaControlado extends DiffProcedenciaBase {
  actividadId?: string;
  /** Los cambios de la validación, en orden de documento. */
  cambios: CambioConProcedencia[];
  /**
   * El documento completo. Si no va, la columna izquierda muestra solo los
   * párrafos tocados: sigue siendo útil, pero se pierde el «antes y después»
   * del documento entero.
   */
  bloques?: Bloque[] | null;
  documentoTitulo?: string | null;
  /** Impactos ya abiertos, agrupados por cambio. Se muestran dentro de su tarjeta. */
  impactos?: GrupoDeImpactos[] | null;
  cargando?: boolean;
  onDecidir?: (cambioId: string, decision: DecisionCambio) => void | Promise<void>;
  /** Id del cambio cuya decisión está en vuelo, para bloquear sus botones. */
  decidiendo?: string | null;
  /** Error de la última decisión, ya en español. */
  error?: string | null;
  /** D08: con impactos pendientes la versión no se cierra. Se avisa arriba. */
  puedeCerrarse?: boolean;
  motivoCierre?: string;
}

export type DiffProcedenciaProps = DiffProcedenciaAutonomo | DiffProcedenciaControlado;

/**
 * Adentro las dos formas son la misma con campos que pueden faltar. Se estrecha
 * una sola vez, acá, y no en cada lectura: la unión es una promesa para quien
 * llama, no una condición que el cuerpo tenga que volver a probar veinte veces.
 */
type PropsInternas = DiffProcedenciaBase & Partial<DiffProcedenciaControlado>;

/** Qué se lee en la columna del documento. */
type Vista = 'antes' | 'despues';

type CapaId = 'original' | 'voces' | 'acuerdo' | 'final';

const TODAS_LAS_CAPAS: CapaId[] = ['original', 'voces', 'acuerdo', 'final'];

/** Cuántos párrafos se pintan de golpe. Un documento de 636 no arranca entero. */
const TRAMO = 120;

// ═══════════════════════════════════════════════════════════════════════════
// Vocabulario visual de las clases
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Cada clase tiene color e icono FIJOS. Nada de clases armadas al vuelo: el
 * compilador de Tailwind no ve las cadenas construidas en tiempo de ejecución y
 * el color se caería en producción sin avisar en desarrollo.
 */
const ACENTO_CLASE: Record<ClaseCambio, { texto: string; borde: string; fondo: string }> = {
  convergente: { texto: 'text-success', borde: 'border-success', fondo: 'bg-success/10' },
  complementaria: { texto: 'text-info', borde: 'border-info', fondo: 'bg-info/10' },
  contradictoria: { texto: 'text-warning', borde: 'border-warning', fondo: 'bg-warning/10' },
  sin_cambio: {
    texto: 'text-content-muted',
    borde: 'border-border-strong',
    fondo: 'bg-surface-sunken',
  },
};

function IconoClase({ clase, className }: { clase: ClaseCambio; className?: string }) {
  if (clase === 'contradictoria') return <Scale className={className} aria-hidden="true" />;
  if (clase === 'complementaria') return <GitMerge className={className} aria-hidden="true" />;
  if (clase === 'sin_cambio') return <ScrollText className={className} aria-hidden="true" />;
  return <Users className={className} aria-hidden="true" />;
}

function acento(clase: ClaseCambio) {
  return ACENTO_CLASE[clase] ?? ACENTO_CLASE.sin_cambio;
}

// ═══════════════════════════════════════════════════════════════════════════
// El ancho decide la forma, y se pregunta una sola vez
// ═══════════════════════════════════════════════════════════════════════════

function useEsEscritorio(): boolean {
  const consulta = '(min-width: 1024px)';
  const [ancho, setAncho] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(consulta).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(consulta);
    const alCambiar = (e: MediaQueryListEvent) => setAncho(e.matches);
    setAncho(mq.matches);
    mq.addEventListener('change', alCambiar);
    return () => mq.removeEventListener('change', alCambiar);
  }, []);
  return ancho;
}

// ═══════════════════════════════════════════════════════════════════════════
// Componente
// ═══════════════════════════════════════════════════════════════════════════

export function DiffProcedencia(props: DiffProcedenciaProps) {
  const p = props as PropsInternas;
  const {
    actividadId,
    cambioId = null,
    onCerrar,
    puedeDecidir = true,
    seleccionado: seleccionadoProp,
    onSeleccionar,
  } = p;

  // Autónomo o controlado: lo decide si le pasaron los cambios, no una bandera.
  // Una bandera se puede poner mal; esto no.
  const autonomo = p.cambios === undefined;

  const escritorio = useEsEscritorio();
  const qc = useQueryClient();

  // Las tres lecturas son independientes y ninguna bloquea a las otras: el diff
  // se puede leer sin el documento (marcas sueltas) y sin los impactos (todavía
  // no hay ninguno aprobado). Las llaves son las compartidas de `lib/cambios`,
  // así que aprobar acá deja al tablero de la Cabina al día sin coordinación.
  const habilitado = autonomo && Boolean(actividadId);
  const consulta = useQuery({
    queryKey: cambiosKeys.cambios(actividadId ?? ''),
    queryFn: () => cambiosApi.diff(actividadId as string),
    enabled: habilitado,
  });
  const impactosQ = useQuery({
    queryKey: cambiosKeys.impactos(actividadId ?? ''),
    queryFn: () => cambiosApi.impactos(actividadId as string),
    enabled: habilitado,
  });
  const documentoQ = useQuery({
    queryKey: documentosKeys.deValidacion(actividadId ?? ''),
    queryFn: () => documentosApi.deValidacion(actividadId as string),
    enabled: habilitado,
  });

  const [errorPropio, setErrorPropio] = useState<string | null>(null);
  const [decidiendoPropio, setDecidiendoPropio] = useState<string | null>(null);

  const decidirMut = useMutation({
    mutationFn: (v: { cambioId: string; decision: DecisionCambio }) =>
      cambiosApi.decidir(v.cambioId, v.decision),
    onMutate: (v) => {
      setErrorPropio(null);
      setDecidiendoPropio(v.cambioId);
    },
    onSuccess: () => {
      // Aprobar abre impactos: las dos listas cambian a la vez y las dos se
      // vuelven a pedir. Si solo se refrescara el cambio, los párrafos que
      // acaban de quedar tocados no aparecerían y nadie se enteraría.
      void qc.invalidateQueries({ queryKey: cambiosKeys.cambios(actividadId ?? '') });
      void qc.invalidateQueries({ queryKey: cambiosKeys.impactos(actividadId ?? '') });
    },
    onError: (e: unknown) =>
      setErrorPropio(
        e instanceof ApiError
          ? e.detail
          : 'No pudimos guardar la decisión. Nada quedó a medias: reintentá en un momento.',
      ),
    onSettled: () => setDecidiendoPropio(null),
  });

  // ── De acá para abajo da igual de dónde vinieron los datos ────────────────
  const cambios = useMemo(
    () => (autonomo ? consulta.data?.cambios ?? [] : p.cambios ?? []),
    [autonomo, consulta.data, p.cambios],
  );
  const bloques = autonomo ? documentoQ.data?.bloques ?? null : p.bloques ?? null;
  const impactos = autonomo ? impactosQ.data?.grupos ?? null : p.impactos ?? null;
  const titulo = autonomo ? consulta.data?.documento_titulo ?? null : p.documentoTitulo ?? null;
  const cargando = autonomo ? consulta.isLoading : Boolean(p.cargando);
  const puedeCerrarse = autonomo ? consulta.data?.puede_cerrarse ?? true : p.puedeCerrarse ?? true;
  const motivoCierre = autonomo ? consulta.data?.motivo_cierre ?? '' : p.motivoCierre ?? '';
  const decidiendo = autonomo ? decidiendoPropio : p.decidiendo ?? null;
  const errorDecision = autonomo ? errorPropio : p.error ?? null;
  const decidir = (id: string, decision: DecisionCambio) => {
    if (autonomo) decidirMut.mutate({ cambioId: id, decision });
    else void p.onDecidir?.(id, decision);
  };

  const [seleccionInterna, setSeleccionInterna] = useState<string | null>(cambioId);
  // Quien abre el diff desde la lista de la Cabina ya eligió: se respeta.
  useEffect(() => setSeleccionInterna(cambioId), [cambioId]);
  const seleccionado = seleccionadoProp !== undefined ? seleccionadoProp : seleccionInterna;
  const elegir = useCallback(
    (id: string | null) => {
      setErrorPropio(null);
      // Soltar la marca es volver a donde estaba quien abrió esto, si hay a
      // dónde volver. En un teléfono, quedarse en un documento de seiscientos
      // párrafos sin nada seleccionado es quedarse sin salida visible.
      if (id === null && onCerrar) {
        onCerrar();
        return;
      }
      setSeleccionInterna(id);
      onSeleccionar?.(id);
    },
    [onCerrar, onSeleccionar],
  );

  /**
   * Soltar la marca sin salir del diff. Es lo que hace volver a tocar el párrafo
   * ya elegido: plegar una tarjeta no puede sacar a nadie de la pantalla, y por
   * eso no pasa por {@link elegir} —que sí es una salida cuando hay `onCerrar`—.
   */
  const soltar = useCallback(
    (id: string | null) => {
      setErrorPropio(null);
      setSeleccionInterna(id);
      onSeleccionar?.(id);
    },
    [onSeleccionar],
  );

  const [vista, setVista] = useState<Vista>('antes');
  const [soloTocados, setSoloTocados] = useState(false);

  // Los borradores viven acá arriba y no dentro de la tarjeta: la tarjeta se
  // desmonta al cambiar de marca, y perder una redacción a medio escribir por
  // haber ido a mirar otro párrafo sería imperdonable.
  const [borradores, setBorradores] = useState<Record<string, string>>({});
  const [motivos, setMotivos] = useState<Record<string, string>>({});

  const porBloque = useMemo(() => indiceCambiosPorBloque(cambios), [cambios]);
  const cambioSel = useMemo(
    () => cambios.find((c) => c.id === seleccionado) ?? null,
    [cambios, seleccionado],
  );

  // En escritorio las cuatro capas se ven de una: hay sitio. En un teléfono se
  // abre la que decide —el acuerdo— y las demás quedan a un toque.
  const [abiertas, setAbiertas] = useState<CapaId[]>(TODAS_LAS_CAPAS);
  useEffect(() => {
    setAbiertas(escritorio ? TODAS_LAS_CAPAS : ['acuerdo']);
  }, [escritorio, seleccionado]);
  const alternarCapa = (capa: CapaId) =>
    setAbiertas((prev) => (prev.includes(capa) ? prev.filter((c) => c !== capa) : [...prev, capa]));

  if (cargando) {
    return (
      <section className="rounded-xl border border-border bg-surface-raised p-4 shadow-sm">
        <EsqueletoLineas lineas={10} />
      </section>
    );
  }

  if (autonomo && consulta.isError) {
    return (
      <section className="rounded-xl border border-error bg-surface-raised p-4">
        <p className="font-sans text-sm leading-relaxed text-content">
          No pudimos traer el diff. Nada se perdió: los comentarios y lo ya decidido
          están a salvo. Reintentá en un momento.
        </p>
      </section>
    );
  }

  // `null` es el camino normal de una validación sin documento cargado —las tres
  // vivas del Consejo están así—, no un error.
  if (autonomo && !consulta.data) {
    return (
      <section className="rounded-xl border border-dashed border-border bg-surface-raised p-5 text-center">
        <FileText className="mx-auto h-6 w-6 text-content-muted" aria-hidden="true" />
        <p className="mt-2 font-sans text-sm leading-relaxed text-content-muted">
          Esta validación no tiene un documento cargado en ÁGORA: los comentarios
          entraron sobre el documento completo, así que no hay párrafos que comparar.
        </p>
      </section>
    );
  }

  if (cambios.length === 0) {
    return (
      <section className="rounded-xl border border-dashed border-border bg-surface-raised p-5 text-center">
        <FileText className="mx-auto h-6 w-6 text-content-muted" aria-hidden="true" />
        <p className="mt-2 font-sans text-sm leading-relaxed text-content-muted">
          Todavía no hay cambios propuestos sobre este documento. Cuando se consolide
          lo comentado, cada párrafo tocado va a aparecer acá con las voces que lo
          piden.
        </p>
      </section>
    );
  }

  const tarjeta = (c: CambioConProcedencia) => (
    <TarjetaCambio
      cambio={c}
      grupo={impactosDe(impactos, c.id)}
      abiertas={abiertas}
      onAlternarCapa={alternarCapa}
      borrador={borradores[c.id] ?? borradorInicial(c)}
      onBorrador={(t) => setBorradores((prev) => ({ ...prev, [c.id]: t }))}
      motivo={motivos[c.id] ?? ''}
      onMotivo={(t) => setMotivos((prev) => ({ ...prev, [c.id]: t }))}
      puedeDecidir={puedeDecidir}
      onDecidir={decidir}
      decidiendo={decidiendo === c.id}
      error={errorDecision}
      onCerrar={() => elegir(null)}
      mostrarCerrar
    />
  );

  const columnaDocumento = (
    <ColumnaDocumento
      bloques={bloques ?? null}
      cambios={cambios}
      porBloque={porBloque}
      vista={vista}
      soloTocados={soloTocados}
      seleccionado={seleccionado}
      onElegir={(id) => soltar(id === seleccionado ? null : id)}
      // En móvil la tarjeta se apila DEBAJO de la marca que se tocó: es la
      // relación entre una y otra explicada sin una línea de texto.
      renderTarjeta={escritorio ? undefined : (c) => tarjeta(c)}
    />
  );

  return (
    <section className="flex flex-col gap-3">
      <Cabecera
        titulo={titulo}
        cambios={cambios}
        vista={vista}
        onVista={setVista}
        soloTocados={soloTocados}
        onSoloTocados={setSoloTocados}
        hayDocumento={Boolean(bloques && bloques.length > 0)}
        puedeCerrarse={puedeCerrarse}
        motivoCierre={motivoCierre}
      />

      {escritorio ? (
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,26rem)] items-start gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,32rem)]">
          {columnaDocumento}
          <div className="sticky top-3 max-h-[calc(100vh-1.5rem)] overflow-y-auto">
            {cambioSel ? (
              tarjeta(cambioSel)
            ) : (
              <div className="rounded-xl border border-dashed border-border bg-surface-raised p-5 text-center">
                <Sparkles className="mx-auto h-6 w-6 text-content-muted" aria-hidden="true" />
                <p className="mt-2 font-sans text-sm leading-relaxed text-content-muted">
                  Tocá un párrafo marcado, a la izquierda, para ver quién pidió ese
                  cambio, qué se propone y por qué.
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        columnaDocumento
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Cabecera: cuántos, de qué clase, y si la versión puede cerrarse
// ═══════════════════════════════════════════════════════════════════════════

function Cabecera({
  titulo,
  cambios,
  vista,
  onVista,
  soloTocados,
  onSoloTocados,
  hayDocumento,
  puedeCerrarse,
  motivoCierre,
}: {
  titulo?: string | null;
  cambios: CambioConProcedencia[];
  vista: Vista;
  onVista: (v: Vista) => void;
  soloTocados: boolean;
  onSoloTocados: (v: boolean) => void;
  hayDocumento: boolean;
  puedeCerrarse: boolean;
  motivoCierre: string;
}) {
  const conteos = useMemo(() => {
    const m = new Map<ClaseCambio, number>();
    for (const c of cambios) m.set(c.clase, (m.get(c.clase) ?? 0) + 1);
    return m;
  }, [cambios]);

  const contradicciones = conteos.get('contradictoria') ?? 0;

  return (
    <header className="rounded-xl border border-border bg-surface-raised p-3 shadow-sm">
      {titulo && (
        <p className="flex items-center gap-2 font-display text-sm font-semibold text-content">
          <FileText className="h-4 w-4 shrink-0 text-content-muted" aria-hidden="true" />
          <span className="min-w-0 truncate">{titulo}</span>
        </p>
      )}

      <p className="mt-1 font-sans text-xs leading-relaxed text-content-muted">
        {cambios.length === 1
          ? '1 párrafo cambia'
          : `${cambios.length} párrafos cambian`}
        , y cada uno muestra quién lo pidió y con qué palabras.
      </p>

      <ul className="mt-2 flex flex-wrap gap-1.5">
        {(['convergente', 'complementaria', 'contradictoria', 'sin_cambio'] as ClaseCambio[])
          .filter((clase) => (conteos.get(clase) ?? 0) > 0)
          .map((clase) => {
            const a = acento(clase);
            return (
              <li
                key={clase}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2 py-1',
                  'font-sans text-xs font-semibold',
                  a.borde,
                  a.texto,
                )}
              >
                <IconoClase clase={clase} className="h-3.5 w-3.5" />
                {conteos.get(clase)} · {ETIQUETA_CLASE[clase]}
              </li>
            );
          })}
      </ul>

      {contradicciones > 0 && (
        <p className="mt-2 flex items-start gap-2 rounded-lg border border-warning px-2.5 py-2 font-sans text-xs leading-relaxed text-content">
          <Scale className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
          <span>
            <strong className="font-semibold">
              {contradicciones === 1
                ? '1 párrafo con posiciones enfrentadas'
                : `${contradicciones} párrafos con posiciones enfrentadas`}
            </strong>
            . No traen redacción propuesta y no es un fallo: lo que se pidió es
            incompatible entre sí y lo tiene que dirimir una persona.
          </span>
        </p>
      )}

      {!puedeCerrarse && motivoCierre && (
        <p className="mt-2 flex items-start gap-2 rounded-lg border border-error px-2.5 py-2 font-sans text-xs leading-relaxed text-content">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-error" aria-hidden="true" />
          <span>
            <strong className="font-semibold">La versión todavía no se puede cerrar.</strong>{' '}
            {motivoCierre}
          </span>
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div
          role="group"
          aria-label="Cómo se lee el documento"
          className="inline-flex gap-1 rounded-lg border border-border-strong bg-surface-base p-1"
        >
          <BotonAlterno
            activo={vista === 'antes'}
            onClick={() => onVista('antes')}
            etiqueta="Como está"
          />
          <BotonAlterno
            activo={vista === 'despues'}
            onClick={() => onVista('despues')}
            etiqueta="Como quedaría"
          />
        </div>

        {/* Solo tiene sentido si hay documento entero del cual recortar. Sin él,
            la columna YA es la lista de párrafos tocados. */}
        {hayDocumento && (
          <button
            type="button"
            aria-pressed={soloTocados}
            onClick={() => onSoloTocados(!soloTocados)}
            className={cn(
              'inline-flex min-h-touch items-center gap-1.5 rounded-lg border px-3',
              'font-sans text-sm font-medium transition-colors duration-base ease-salida',
              'focus-visible:outline-none focus-visible:shadow-focus',
              soloTocados
                ? 'border-primary bg-primary/10 text-content'
                : 'border-border-strong bg-surface-base text-content-muted hover:text-content',
            )}
          >
            Solo lo que cambia
          </button>
        )}
      </div>
    </header>
  );
}

function BotonAlterno({
  activo,
  onClick,
  etiqueta,
}: {
  activo: boolean;
  onClick: () => void;
  etiqueta: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onClick}
      className={cn(
        'inline-flex min-h-touch items-center rounded-md px-3',
        'font-sans text-sm font-medium transition-colors duration-base ease-salida',
        'focus-visible:outline-none focus-visible:shadow-focus',
        activo ? 'bg-primary text-content-on-primary' : 'text-content-muted hover:text-content',
      )}
    >
      {etiqueta}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Columna del documento: el texto completo con las marcas clickeables
// ═══════════════════════════════════════════════════════════════════════════

function ColumnaDocumento({
  bloques,
  cambios,
  porBloque,
  vista,
  soloTocados,
  seleccionado,
  onElegir,
  renderTarjeta,
}: {
  bloques: Bloque[] | null;
  cambios: CambioConProcedencia[];
  porBloque: Map<string, CambioConProcedencia>;
  vista: Vista;
  soloTocados: boolean;
  seleccionado: string | null;
  onElegir: (cambioId: string) => void;
  /** Solo en móvil: la tarjeta que se apila debajo de la marca elegida. */
  renderTarjeta?: (cambio: CambioConProcedencia) => ReactNode;
}) {
  const [tope, setTope] = useState(TRAMO);

  // Sin documento cargado la columna es la lista de párrafos tocados. Se pierde
  // el «antes y después» del documento entero, pero lo que se decide se decide
  // igual: nunca se deja de mostrar el cambio por no tener el resto.
  const filas: Array<{ clave: string; bloque: Bloque | null; cambio: CambioConProcedencia | null }> =
    useMemo(() => {
      if (bloques && bloques.length > 0) {
        const todas = bloques.map((b) => ({
          clave: b.id_estable,
          bloque: b,
          cambio: porBloque.get(b.id_estable) ?? null,
        }));
        return soloTocados ? todas.filter((f) => f.cambio) : todas;
      }
      return cambios.map((c) => ({ clave: c.id, bloque: null, cambio: c }));
    }, [bloques, cambios, porBloque, soloTocados]);

  // Un documento de seiscientos párrafos no se pinta entero, pero la marca que
  // alguien acaba de elegir TIENE que existir en la pantalla: si el cambio está
  // en el párrafo 400 y solo hay 120 pintados, no hay nodo al que ir y la
  // columna se queda muda mientras el panel de la derecha muestra otra cosa.
  const indiceSel = useMemo(
    () => (seleccionado ? filas.findIndex((f) => f.cambio?.id === seleccionado) : -1),
    [filas, seleccionado],
  );
  useEffect(() => {
    if (indiceSel >= tope) setTope(Math.ceil((indiceSel + 1) / TRAMO) * TRAMO);
  }, [indiceSel, tope]);

  // La marca elegida desde el panel se trae a la vista. Si el toque fue acá no
  // se mueve nada: saltar bajo el dedo de quien acaba de tocar es desorientador.
  const nodos = useRef(new Map<string, HTMLLIElement>());
  const tocadoAca = useRef(false);
  const yaTraido = useRef<string | null>(null);
  useEffect(() => {
    if (!seleccionado) {
      yaTraido.current = null;
      return;
    }
    if (yaTraido.current === seleccionado) return;
    if (tocadoAca.current) {
      tocadoAca.current = false;
      yaTraido.current = seleccionado;
      return;
    }
    const el = nodos.current.get(seleccionado);
    if (!el) return;
    yaTraido.current = seleccionado;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [seleccionado, tope]);

  const visibles = filas.slice(0, tope);
  const restantes = filas.length - visibles.length;

  return (
    <section className="rounded-xl border border-border bg-surface-raised shadow-sm">
      <ol className="flex flex-col gap-1 px-2 py-3">
        {visibles.map(({ clave, bloque, cambio }) => {
          const activo = Boolean(cambio && cambio.id === seleccionado);
          return (
            <li
              key={clave}
              ref={(el) => {
                if (!cambio) return;
                if (el) nodos.current.set(cambio.id, el);
                else nodos.current.delete(cambio.id);
              }}
              className="scroll-mt-24"
            >
              {cambio ? (
                <MarcaCambio
                  cambio={cambio}
                  bloque={bloque}
                  vista={vista}
                  activo={activo}
                  onElegir={() => {
                    tocadoAca.current = true;
                    onElegir(cambio.id);
                  }}
                />
              ) : (
                <ParrafoQuieto bloque={bloque as Bloque} />
              )}
              {activo && cambio && renderTarjeta && (
                <div className="despliega mt-2">{renderTarjeta(cambio)}</div>
              )}
            </li>
          );
        })}
      </ol>

      {restantes > 0 && (
        <div className="px-3 pb-3">
          <button
            type="button"
            onClick={() => setTope((t) => t + TRAMO)}
            className="flex min-h-touch w-full items-center justify-center gap-2 rounded-lg border border-border-strong bg-surface-base px-3 font-sans text-sm font-medium text-content transition-colors duration-base ease-salida hover:border-primary focus-visible:outline-none focus-visible:shadow-focus"
          >
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
            Mostrar más del documento ({restantes}{' '}
            {restantes === 1 ? 'párrafo restante' : 'párrafos restantes'})
          </button>
        </div>
      )}
    </section>
  );
}

/** Un párrafo que nadie tocó: se lee, no se toca. */
function ParrafoQuieto({ bloque }: { bloque: Bloque }) {
  const esTitulo = bloque.tipo === 'titulo';
  return (
    <div className="px-3 py-1.5">
      {bloque.ref && (
        <span className="mb-1 inline-block rounded-md border border-border bg-surface-sunken px-1.5 py-0.5 font-sans text-xs font-semibold tracking-wide text-content-muted">
          {bloque.ref}
        </span>
      )}
      <p
        className={cn(
          'font-sans leading-relaxed',
          esTitulo
            ? 'font-display text-base font-semibold text-content'
            : 'text-sm text-content-muted',
        )}
      >
        {bloque.texto}
      </p>
    </div>
  );
}

/** Un párrafo tocado: la marca clickeable que abre las cuatro capas. */
function MarcaCambio({
  cambio,
  bloque,
  vista,
  activo,
  onElegir,
}: {
  cambio: CambioConProcedencia;
  bloque: Bloque | null;
  vista: Vista;
  activo: boolean;
  onElegir: () => void;
}) {
  const a = acento(cambio.clase);
  const texto = textoSegunVista(cambio, vista);
  const cambiado = vista === 'despues' && texto !== cambio.original;

  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onElegir}
      className={cn(
        'block w-full rounded-lg border-2 border-l-4 px-3 py-2.5 text-left',
        'transition-colors duration-base ease-salida',
        'focus-visible:outline-none focus-visible:shadow-focus',
        a.borde,
        activo ? a.fondo : 'hover:bg-surface-sunken',
      )}
    >
      <span className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="rounded-md border border-border bg-surface-sunken px-1.5 py-0.5 font-sans text-xs font-semibold tracking-wide text-content">
          {etiquetaBloque(cambio.ref ?? bloque?.ref ?? null, cambio.orden)}
        </span>
        <span className={cn('inline-flex items-center gap-1 font-sans text-xs font-semibold', a.texto)}>
          <IconoClase clase={cambio.clase} className="h-3.5 w-3.5" />
          {ETIQUETA_CLASE[cambio.clase]}
        </span>
        <span className="font-sans text-xs text-content-muted">
          {ETIQUETA_ESTADO[cambio.estado] ?? cambio.estado}
        </span>
        {cambio.impactos_abiertos > 0 && (
          <span className="inline-flex items-center gap-1 font-sans text-xs font-semibold text-warning">
            <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
            {cambio.impactos_abiertos} sin revisar
          </span>
        )}
      </span>

      <span className="block font-sans text-sm leading-relaxed text-content">{texto}</span>

      {cambiado && (
        <span className="mt-1 block font-sans text-xs text-content-muted">
          Así quedaría este párrafo si se aprueba.
        </span>
      )}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// La tarjeta: las cuatro capas
// ═══════════════════════════════════════════════════════════════════════════

function TarjetaCambio({
  cambio,
  grupo,
  abiertas,
  onAlternarCapa,
  borrador,
  onBorrador,
  motivo,
  onMotivo,
  puedeDecidir,
  onDecidir,
  decidiendo,
  error,
  onCerrar,
  mostrarCerrar,
}: {
  cambio: CambioConProcedencia;
  grupo: GrupoDeImpactos | null;
  abiertas: CapaId[];
  onAlternarCapa: (capa: CapaId) => void;
  borrador: string;
  onBorrador: (t: string) => void;
  motivo: string;
  onMotivo: (t: string) => void;
  puedeDecidir: boolean;
  onDecidir?: (cambioId: string, decision: DecisionCambio) => void | Promise<void>;
  decidiendo: boolean;
  error: string | null;
  onCerrar: () => void;
  mostrarCerrar: boolean;
}) {
  const a = acento(cambio.clase);
  const decidido = yaDecidido(cambio);
  const terminal = esTerminal(cambio);
  const contradiccion = esContradiccion(cambio);

  const decidir = (accion: DecisionCambio['accion']) => {
    if (!onDecidir) return;
    const texto = borrador.trim();
    onDecidir(cambio.id, {
      accion,
      texto_final: texto || undefined,
      motivo: motivo.trim() || undefined,
    });
  };

  // El botón no inventa una regla propia: repite la del servidor, con el único
  // dato que el servidor todavía no tiene —lo que se está escribiendo acá—.
  const porqueNo = bloqueoParaAprobar(cambio, borrador);
  const bloqueaAprobar = Boolean(porqueNo);

  return (
    <article className="rounded-xl border border-border bg-surface-raised shadow-sm">
      {/* Encabezado */}
      <header className={cn('rounded-t-xl border-b px-3 py-3', a.borde, a.fondo)}>
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="rounded-md border border-border bg-surface-raised px-1.5 py-0.5 font-sans text-xs font-semibold tracking-wide text-content">
                {etiquetaBloque(cambio.ref, cambio.orden)}
              </span>
              <span className={cn('inline-flex items-center gap-1 font-sans text-xs font-semibold', a.texto)}>
                <IconoClase clase={cambio.clase} className="h-3.5 w-3.5" />
                {ETIQUETA_CLASE[cambio.clase]}
              </span>
              <span className="font-sans text-xs text-content-muted">
                {ETIQUETA_ESTADO[cambio.estado] ?? cambio.estado}
              </span>
              {cambio.propuesto_por === 'ia' && (
                <span className="inline-flex items-center gap-1 rounded-full border border-violet px-2 py-0.5 font-sans text-xs font-semibold text-violet">
                  <Sparkles className="h-3 w-3" aria-hidden="true" />
                  Redactado por IA
                </span>
              )}
            </p>
            {cambio.seccion && (
              <p className="mt-1 font-sans text-xs leading-relaxed text-content-muted">
                {cambio.seccion}
              </p>
            )}
          </div>
          {mostrarCerrar && (
            <button
              type="button"
              onClick={onCerrar}
              aria-label="Cerrar el panel del cambio"
              className="-my-1 -mr-1 flex min-h-touch min-w-touch items-center justify-center rounded-md text-content-muted transition-colors duration-fast hover:text-content focus-visible:outline-none focus-visible:shadow-focus"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>

        <p className="mt-2 font-sans text-xs leading-relaxed text-content">
          {AYUDA_CLASE[cambio.clase]}
        </p>
      </header>

      {cambio.no_respaldado && (
        <p className="flex items-start gap-2 border-b border-border px-3 py-2 font-sans text-xs leading-relaxed text-content">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-error" aria-hidden="true" />
          <span>
            <strong className="font-semibold">Sin voz que lo respalde.</strong> Lo propuso
            la inteligencia artificial y ninguna de sus citas resistió la verificación. Así
            no se aprueba: o se ubica el comentario que lo pide, o se escribe a nombre
            propio.
          </span>
        </p>
      )}

      <div className="flex flex-col divide-y divide-border">
        {/* ── Capa 1 · Original ─────────────────────────────────────────── */}
        <Capa
          numero={1}
          capa="original"
          titulo="Como está hoy"
          icono={<ScrollText className="h-4 w-4" aria-hidden="true" />}
          abierta={abiertas.includes('original')}
          onAlternar={onAlternarCapa}
        >
          <p className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-content">
            {cambio.original}
          </p>
        </Capa>

        {/* ── Capa 2 · Voces ────────────────────────────────────────────── */}
        <Capa
          numero={2}
          capa="voces"
          titulo="Quién lo pidió"
          icono={<MessageSquareQuote className="h-4 w-4" aria-hidden="true" />}
          resumen={
            cambio.voces.length === 1 ? '1 voz' : `${cambio.voces.length} voces`
          }
          abierta={abiertas.includes('voces')}
          onAlternar={onAlternarCapa}
        >
          {cambio.voces.length === 0 ? (
            <p className="font-sans text-sm leading-relaxed text-content-muted">
              Ninguna voz quedó citada acá. Si el cambio lo propuso la inteligencia
              artificial, eso es una alarma y no un detalle: nada se aprueba sin
              procedencia.
            </p>
          ) : (
            <ul className="flex flex-col gap-2.5">
              {cambio.voces.map((v) => (
                <Voz key={v.aporte_id} voz={v} />
              ))}
            </ul>
          )}
        </Capa>

        {/* ── Capa 3 · Acuerdo ──────────────────────────────────────────── */}
        <Capa
          numero={3}
          capa="acuerdo"
          titulo={contradiccion ? 'Las dos posiciones' : 'Lo que se propone'}
          icono={
            contradiccion ? (
              <Scale className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Sparkles className="h-4 w-4" aria-hidden="true" />
            )
          }
          abierta={abiertas.includes('acuerdo')}
          onAlternar={onAlternarCapa}
        >
          {contradiccion ? (
            <Posiciones
              posiciones={cambio.posiciones}
              puedeDecidir={puedeDecidir && !decidido}
              onTomar={(postura) => onBorrador(postura)}
              elegida={borrador}
            />
          ) : cambio.propuesto ? (
            <>
              <p className="whitespace-pre-wrap rounded-lg border border-border bg-surface-sunken px-3 py-2 font-sans text-sm leading-relaxed text-content">
                {cambio.propuesto}
              </p>
              {cambio.motivo && (
                <p className="mt-2 font-sans text-xs leading-relaxed text-content-muted">
                  <span className="font-semibold text-content">Por qué: </span>
                  {cambio.motivo}
                </p>
              )}
            </>
          ) : (
            <p className="font-sans text-sm leading-relaxed text-content-muted">
              No hay redacción propuesta para este párrafo.
              {cambio.motivo ? ` ${cambio.motivo}` : ''}
            </p>
          )}

          {cambio.escalada && (
            <p className="mt-2 flex items-start gap-2 rounded-lg border border-warning px-2.5 py-2 font-sans text-xs leading-relaxed text-content">
              <Scale className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
              <span>
                Esto quedó escalado a propósito. La decisión es de una persona, con
                nombre, y queda en el expediente.
              </span>
            </p>
          )}
        </Capa>

        {/* ── Capa 4 · Final ────────────────────────────────────────────── */}
        <Capa
          numero={4}
          capa="final"
          titulo="Cómo va a quedar"
          icono={<PenLine className="h-4 w-4" aria-hidden="true" />}
          abierta={abiertas.includes('final')}
          onAlternar={onAlternarCapa}
        >
          {decidido ? (
            <Aprobado
              cambio={cambio}
              // Volver sobre lo decidido se puede hasta que el cambio esté
              // escrito en el documento. Después no: se corrige en la versión
              // siguiente, y el servidor lo rechaza con 409.
              puedeReabrir={puedeDecidir && !terminal && Boolean(onDecidir)}
              reabriendo={decidiendo}
              onReabrir={() => decidir('en_discusion')}
            />
          ) : (
            <CapaFinal
              cambio={cambio}
              borrador={borrador}
              onBorrador={onBorrador}
              motivo={motivo}
              onMotivo={onMotivo}
              puedeDecidir={puedeDecidir}
              bloqueaAprobar={bloqueaAprobar}
              porqueNo={porqueNo}
              decidiendo={decidiendo}
              onDecidir={decidir}
            />
          )}

          {error && (
            <p className="mt-2 font-sans text-xs leading-relaxed text-error">{error}</p>
          )}
        </Capa>
      </div>

      {grupo && grupo.impactos.length > 0 && <Impactos grupo={grupo} />}
    </article>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Una capa del acordeón
// ═══════════════════════════════════════════════════════════════════════════

function Capa({
  numero,
  capa,
  titulo,
  icono,
  resumen,
  abierta,
  onAlternar,
  children,
}: {
  numero: number;
  capa: CapaId;
  titulo: string;
  icono: ReactNode;
  resumen?: string;
  abierta: boolean;
  onAlternar: (capa: CapaId) => void;
  children: ReactNode;
}) {
  const id = `capa-${capa}`;
  return (
    <section>
      <h3>
        <button
          type="button"
          aria-expanded={abierta}
          aria-controls={id}
          onClick={() => onAlternar(capa)}
          className="flex min-h-touch w-full items-center gap-2 px-3 py-2 text-left transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline-none focus-visible:shadow-focus"
        >
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border font-sans text-xs font-semibold text-content-muted">
            {numero}
          </span>
          <span className="shrink-0 text-content-muted">{icono}</span>
          <span className="min-w-0 flex-1 font-sans text-sm font-semibold text-content">
            {titulo}
          </span>
          {resumen && (
            <span className="shrink-0 font-sans text-xs text-content-muted">{resumen}</span>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-content-muted',
              'transition-transform duration-base ease-salida',
              abierta && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </button>
      </h3>
      {abierta && (
        <div id={id} className="despliega px-3 pb-3">
          {children}
        </div>
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Capa 2 · una voz, con su audio
// ═══════════════════════════════════════════════════════════════════════════

function Voz({ voz }: { voz: VozDelCambio }) {
  return (
    <li className="border-l-2 border-border pl-2.5">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-sans text-xs">
        <span className="font-semibold text-content">{voz.autor ?? 'Sin autor registrado'}</span>
        {voz.rol && <span className="text-content-muted">{voz.rol}</span>}
        {voz.que_aporta && (
          <span className="text-content-muted">
            · {ETIQUETA_QUE_APORTA[voz.que_aporta] ?? voz.que_aporta}
          </span>
        )}
        {voz.tiene_audio && <span className="text-content-muted">· de viva voz</span>}
      </p>
      <p className="mt-0.5 font-sans text-sm leading-relaxed text-content">{voz.texto}</p>
      {voz.tiene_audio && <BotonAudio aporteId={voz.aporte_id} />}
    </li>
  );
}

/**
 * El audio original de una intervención. Se descarga solo cuando alguien lo
 * pide: la capa 2 de un cambio con nueve voces no puede traerse nueve archivos
 * de audio por si acaso, sobre todo en la red de una sala.
 */
function BotonAudio({ aporteId }: { aporteId: string }) {
  const [abierto, setAbierto] = useState(false);
  const { url, cargando, error } = useMediaUrl(abierto ? aporteId : null);

  if (!abierto) {
    return (
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="mt-1 inline-flex min-h-touch items-center gap-1.5 rounded-md px-2 font-sans text-xs font-semibold text-primary transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline-none focus-visible:shadow-focus"
      >
        <Volume2 className="h-4 w-4" aria-hidden="true" />
        Escuchar cómo lo dijo
      </button>
    );
  }

  if (cargando) {
    return (
      <p className="mt-1 inline-flex items-center gap-1.5 px-2 font-sans text-xs text-content-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Trayendo el audio…
      </p>
    );
  }

  if (error || !url) {
    return (
      <p className="mt-1 px-2 font-sans text-xs text-error">
        {error ?? 'No pudimos traer el audio. La transcripción de arriba sigue siendo válida.'}
      </p>
    );
  }

  return (
    <audio
      controls
      src={url}
      className="despliega mt-1.5 w-full"
      aria-label="Audio original de la intervención"
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Capa 3 · las posiciones enfrentadas
// ═══════════════════════════════════════════════════════════════════════════

function Posiciones({
  posiciones,
  puedeDecidir,
  onTomar,
  elegida,
}: {
  posiciones: PosicionEnfrentada[];
  puedeDecidir: boolean;
  onTomar: (postura: string) => void;
  elegida: string;
}) {
  if (posiciones.length === 0) {
    return (
      <p className="font-sans text-sm leading-relaxed text-content-muted">
        La síntesis marcó este párrafo como contradictorio pero no dejó las posturas
        escritas. Hay que volver a las voces de arriba y redactar la decisión a mano.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {posiciones.map((p, i) => {
        const tomada = elegida.trim() === p.postura.trim();
        return (
          <li
            key={`${i}-${p.postura.slice(0, 24)}`}
            className={cn(
              'rounded-lg border px-3 py-2.5',
              tomada ? 'border-primary bg-primary/10' : 'border-border bg-surface-sunken',
            )}
          >
            <p className="font-sans text-xs font-semibold uppercase tracking-wide text-content-muted">
              Posición {i + 1}
            </p>
            <p className="mt-1 whitespace-pre-wrap font-sans text-sm leading-relaxed text-content">
              {p.postura}
            </p>
            {p.sostienen.length > 0 && (
              <p className="mt-1.5 font-sans text-xs leading-relaxed text-content-muted">
                <span className="font-semibold text-content">La sostiene: </span>
                {p.sostienen
                  .map((s) => (s.autor ?? 'Sin autor') + (s.rol ? ` (${s.rol})` : ''))
                  .join(' · ')}
              </p>
            )}
            {puedeDecidir && (
              <button
                type="button"
                onClick={() => onTomar(p.postura)}
                aria-pressed={tomada}
                className={cn(
                  'mt-2 inline-flex min-h-touch items-center rounded-md border px-3',
                  'font-sans text-sm font-semibold transition-colors duration-base ease-salida',
                  'focus-visible:outline-none focus-visible:shadow-focus',
                  tomada
                    ? 'border-primary text-primary'
                    : 'border-border-strong text-content hover:border-primary',
                )}
              >
                {tomada ? 'Es la que se va a aprobar' : 'Tomar esta posición'}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Capa 4 · la redacción final, editable
// ═══════════════════════════════════════════════════════════════════════════

function CapaFinal({
  cambio,
  borrador,
  onBorrador,
  motivo,
  onMotivo,
  puedeDecidir,
  bloqueaAprobar,
  porqueNo,
  decidiendo,
  onDecidir,
}: {
  cambio: CambioConProcedencia;
  borrador: string;
  onBorrador: (t: string) => void;
  motivo: string;
  onMotivo: (t: string) => void;
  puedeDecidir: boolean;
  bloqueaAprobar: boolean;
  porqueNo: string;
  decidiendo: boolean;
  onDecidir: (accion: DecisionCambio['accion']) => void;
}) {
  const segmentos = useMemo(
    () => diffPalabras(cambio.original, borrador),
    [cambio.original, borrador],
  );
  const { quitadas, agregadas } = useMemo(() => resumenDiff(segmentos), [segmentos]);

  if (!puedeDecidir) {
    return (
      <>
        {borrador ? (
          <p className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-content">
            {borrador}
          </p>
        ) : (
          <p className="font-sans text-sm leading-relaxed text-content-muted">
            Todavía no hay redacción final. La decide quien facilita.
          </p>
        )}
      </>
    );
  }

  return (
    <>
      <label
        htmlFor={`final-${cambio.id}`}
        className="mb-1 block font-sans text-xs leading-relaxed text-content-muted"
      >
        Lo que propuso el sistema es un borrador. Esto es lo que se va a aprobar:
        editalo si hace falta.
      </label>
      <textarea
        id={`final-${cambio.id}`}
        value={borrador}
        onChange={(e) => onBorrador(e.target.value)}
        rows={5}
        className="w-full rounded-lg border border-border-strong bg-surface-base px-3 py-2 font-sans text-sm leading-relaxed text-content transition-colors duration-fast focus:border-primary focus-visible:outline-none focus-visible:shadow-focus"
      />

      {borrador.trim() && borrador.trim() !== cambio.original.trim() && (
        <details className="mt-2">
          <summary className="min-h-touch cursor-pointer list-none font-sans text-xs font-semibold text-primary">
            Ver qué cambia palabra por palabra ({quitadas} fuera · {agregadas} nuevas)
          </summary>
          <p className="despliega mt-1.5 whitespace-pre-wrap rounded-lg border border-border bg-surface-sunken px-3 py-2 font-sans text-sm leading-relaxed">
            {segmentos.map((s, i) =>
              s.tipo === 'igual' ? (
                <span key={i} className="text-content">
                  {s.texto}
                </span>
              ) : s.tipo === 'quitado' ? (
                <span key={i} className="bg-error/10 text-error line-through">
                  {s.texto}
                </span>
              ) : (
                <span key={i} className="bg-success/15 font-semibold text-content">
                  {s.texto}
                </span>
              ),
            )}
          </p>
        </details>
      )}

      <label
        htmlFor={`motivo-${cambio.id}`}
        className="mb-1 mt-3 block font-sans text-xs leading-relaxed text-content-muted"
      >
        Por qué se decidió así. Queda en el expediente.
      </label>
      <input
        id={`motivo-${cambio.id}`}
        value={motivo}
        onChange={(e) => onMotivo(e.target.value)}
        placeholder="Opcional, pero dentro de un año va a hacer falta"
        className="w-full rounded-lg border border-border-strong bg-surface-base px-3 py-2 font-sans text-sm text-content transition-colors duration-fast focus:border-primary focus-visible:outline-none focus-visible:shadow-focus"
      />

      {bloqueaAprobar && porqueNo && (
        <p className="mt-2 flex items-start gap-2 rounded-lg border border-warning px-2.5 py-2 font-sans text-xs leading-relaxed text-content">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
          <span>{porqueNo}</span>
        </p>
      )}

      <p className="mt-2 font-sans text-xs leading-relaxed text-content-muted">
        Aprobar no es guardar un texto: se revisan los párrafos relacionados y los que
        queden tocados se abren como pendientes. Mientras haya pendientes la versión no
        se cierra.
      </p>

      <div className="mt-2 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={bloqueaAprobar}
          loading={decidiendo}
          onClick={() => onDecidir('aprobar')}
        >
          Aprobar este cambio
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={decidiendo}
          onClick={() => onDecidir('en_discusion')}
        >
          Dejar en discusión
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={decidiendo}
          onClick={() => onDecidir('descartar')}
        >
          No cambiar el párrafo
        </Button>
      </div>
    </>
  );
}

/** Lo ya decidido: con nombre y fecha, que es lo que hace auditable el expediente. */
function Aprobado({
  cambio,
  puedeReabrir,
  reabriendo,
  onReabrir,
}: {
  cambio: CambioConProcedencia;
  puedeReabrir: boolean;
  reabriendo: boolean;
  onReabrir: () => void;
}) {
  const fecha = cambio.aprobado_at ? new Date(cambio.aprobado_at) : null;
  return (
    <>
      {cambio.estado === 'descartado' ? (
        <p className="font-sans text-sm leading-relaxed text-content-muted">
          La sala decidió no tocar este párrafo. Las voces de arriba siguen contando en
          el expediente: descartar un cambio no borra a quien lo pidió.
        </p>
      ) : (
        <p className="whitespace-pre-wrap rounded-lg border border-success px-3 py-2 font-sans text-sm leading-relaxed text-content">
          {cambio.final ?? cambio.propuesto ?? cambio.original}
        </p>
      )}
      <p className="mt-2 font-sans text-xs leading-relaxed text-content-muted">
        {cambio.aprobado_por_nombre ? `Decidió ${cambio.aprobado_por_nombre}` : 'Decidido'}
        {fecha && ` · ${fecha.toLocaleString('es-GT', { dateStyle: 'medium', timeStyle: 'short' })}`}
      </p>
      {cambio.motivo && (
        <p className="mt-1 font-sans text-xs leading-relaxed text-content-muted">
          <span className="font-semibold text-content">Por qué: </span>
          {cambio.motivo}
        </p>
      )}

      {cambio.estado === 'aplicado' ? (
        <p className="mt-2 font-sans text-xs leading-relaxed text-content-muted">
          Ya está escrito en el documento. Si hay que corregirlo, se corrige en la
          versión siguiente.
        </p>
      ) : (
        puedeReabrir && (
          <div className="mt-2">
            <Button size="sm" variant="ghost" loading={reabriendo} onClick={onReabrir}>
              Volver a discutirlo
            </Button>
          </div>
        )
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Lo que este cambio dejó abierto en el resto del documento
// ═══════════════════════════════════════════════════════════════════════════

function Impactos({ grupo }: { grupo: GrupoDeImpactos }) {
  return (
    <section className="border-t border-border px-3 py-3">
      <h3 className="flex items-center gap-2 font-sans text-sm font-semibold text-content">
        <Link2 className="h-4 w-4 text-content-muted" aria-hidden="true" />
        {grupo.impactos.length === 1
          ? 'Este cambio toca otro párrafo'
          : `Este cambio toca otros ${grupo.impactos.length} párrafos`}
      </h3>
      <p className="mt-0.5 font-sans text-xs leading-relaxed text-content-muted">
        {grupo.abiertos > 0
          ? `${grupo.abiertos} sin revisar. Hasta resolverlos la versión no se cierra.`
          : 'Todos revisados.'}
      </p>
      <ul className="mt-2 flex flex-col gap-2">
        {grupo.impactos.map((i) => (
          <FilaImpacto key={i.id} impacto={i} />
        ))}
      </ul>
    </section>
  );
}

function FilaImpacto({ impacto }: { impacto: ImpactoAbierto }) {
  const pendiente = impacto.estado === 'pendiente';
  return (
    <li
      className={cn(
        'rounded-lg border px-2.5 py-2',
        pendiente ? 'border-warning' : 'border-border bg-surface-sunken',
      )}
    >
      <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-sans text-xs">
        <span className="rounded-md border border-border bg-surface-raised px-1.5 py-0.5 font-semibold tracking-wide text-content">
          {impacto.etiqueta}
        </span>
        <span className={pendiente ? 'font-semibold text-warning' : 'text-content-muted'}>
          {ETIQUETA_ESTADO_IMPACTO[impacto.estado] ?? impacto.estado}
        </span>
        {impacto.profundidad > 1 && (
          <span className="text-content-muted">· a {impacto.profundidad} saltos</span>
        )}
      </p>
      {impacto.porque && (
        <p className="mt-1 font-sans text-xs leading-relaxed text-content-muted">
          {impacto.porque}
        </p>
      )}
      {impacto.razon && (
        <p className="mt-1 font-sans text-xs leading-relaxed text-content-muted">
          <span className="font-semibold text-content">
            {impacto.resuelto_por_nombre ? `${impacto.resuelto_por_nombre}: ` : 'Razón: '}
          </span>
          {impacto.razon}
        </p>
      )}
    </li>
  );
}
